import { FieldError } from "./api";
import type { SessionProfile } from "@dvrt/graphql-widgets/heimdall";
import configureStore from "./store";
import { ControlPosition } from "react-draggable";
import { AnswerOptions } from "@dvrt/jssip";
import { Position, Props } from "react-rnd";
import { MessageDescriptor } from "react-intl";

export type NotificationVariant = "success" | "warning" | "error" | "info";

export type NotificationMessageVars = { [key: string]: any };

export type Notification = {
    show: boolean;
    message: MessageDescriptor;
    variant: NotificationVariant;
    messageVars: NotificationMessageVars;
};

export type State = {
    notification: Notification;
    lastMeetingId: number;
    backendError: BackendError;
    theme: "light" | "dark";
};

export type ConcertoDownloadLink = {
    android: string;
    iphone: string;
    linux: string;
    mac: string;
    windows: string;
};

declare global {
    interface Window {
        tequet: {
            sipUri: string;
            mixerDomain: string;
            proxyUrl: string;
        };
        conferenceNumbers: string[];
        concertoDownloadLink: ConcertoDownloadLink;
        existingLanguageTags: string[];
        debugEnabled: boolean;
        concertoClientJoinBaseUrl: string;
        chattoBaseUrl: string;
        supportedBrowsers: Record<string, string>;
        desktopShareConstraints: MediaStreamConstraints;
        videoConstraintsOverrides: MediaTrackConstraints;
        callOptionsOverrides: AnswerOptions;
        meetingLengthSelectOptions: number[];
        auth: "heimdall" | "concerto";
    }

    interface MediaDevices {
        getDisplayMedia(constraints?: MediaStreamConstraints): Promise<MediaStream>;
    }
}

export type FieldErrorList = FieldError[];

export type OmitTypeName<T extends { __typename?: any }> = Omit<T, "__typename">;

export function removeTypename<T extends { __typename?: any }>(source: T): OmitTypeName<T> {
    if (source.__typename) delete source.__typename;
    return source;
}

export type ItemRect = Position & Props["size"];

export type WindowPanelSettings = {
    position: ControlPosition;
    scale: number;
    opacity: number;
};

export type ConferenceLocalStorageSchema = {
    hidePastMeetingsInList: boolean;
    /** Heimdall's /userinfo claims, fetched by the backend and handed over with each access token. */
    profile: SessionProfile;
    beforeLoginUrl: string;
    windowPanelSettings: Record<string, WindowPanelSettings>;
}

export const defaultConferenceLocalStorageSchema: ConferenceLocalStorageSchema = {
    hidePastMeetingsInList: false,
    profile: null,
    beforeLoginUrl: null,
    windowPanelSettings: {},
};

export type ConferenceSessionStorageSchema = {
    clientIdentifier: string;
};

export const defaultConferenceSessionStorageSchema: ConferenceSessionStorageSchema = {
    clientIdentifier: null,
};

export const backendErrorCodes = [
    "not_authorized",
    "unreachable",
    "expired_access_token",
    "unknown_access_token",
    "cant_reach_heimdall",
    "unknown_invitation",
    "unknown",
] as const;

export type BackendErrorCode = (typeof backendErrorCodes)[number];

/**
 * The backend also answers with the error codes of the GraphQL library, like "INTERNAL_SERVER_ERROR", which have no
 * translation and no handling. They are reported as an unknown error.
 */
export function toBackendErrorCode(code: unknown): BackendErrorCode {
    return backendErrorCodes.includes(code as BackendErrorCode) ? (code as BackendErrorCode) : "unknown";
}

export type BackendError = {
    code: BackendErrorCode;
    details?: string;
};

export type TStore = ReturnType<typeof configureStore>;
